import { faTrash } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  ColDef,
  ModuleRegistry,
  ClientSideRowModelModule,
  DateEditorModule,
  DateFilterModule,
  ColumnAutoSizeModule,
  RowSelectionModule,
  ICellRendererParams,
  CellValueChangedEvent,
  GridApi,
  SortChangedEvent
} from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { AxiosError } from 'axios';
import type { JSX } from 'react';
import { useContext, useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

import PageHeader from '@/components/PageHeader';
import { PRIOR_AUTH_OPTIONS_LIST } from '@/constants/treatability';
import AppContext from '@/contexts/appContext';
import useAPI from '@/hooks/useAPI';
import usePagination from '@/hooks/usePagination';
import InsuranceRepository from '@/repositories/InsuranceRepository';
import JurisdictionRepository, { Jurisdiction } from '@/repositories/JurisdictionRepository';
import ProgramRepository from '@/repositories/ProgramRepository';
import TreatabilityMatchRepository from '@/repositories/TreatabilityMatchRepository';
import { InsurancePlanType } from '@/types/insurancePlanType';
import { InsuranceProvider } from '@/types/insuranceProvider';
import { Program } from '@/types/program';
import { DIRECTION } from '@/types/sort';
import { TreatabilityMatch, TreatabilityMatchBulkUpdatePayload } from '@/types/treatabilityMatch';
import AuditLogLink from '@/ui/components/AuditLogLink';
import AutocompleteMultiSelect from '@/ui/components/AutocompleteMultiSelect';
import Button from '@/ui/components/Button';
import Card from '@/ui/components/Card';
import Container from '@/ui/components/Container';
import { OptionType } from '@/ui/components/Form/Select/types';
import Paginator from '@/ui/components/Paginator';
import Spinner from '@/ui/components/Spinner';
import { resolveGridTheme } from '@/utils/gridTheme';
import mapAxiosErrorMessages from '@/utils/mapAxiosErrorMessages';

import BulkAddModal from './BulkAddModal';
import BulkEditModal from './BulkEditModal';
import type { BulkEditData } from './BulkEditModal/types';

ModuleRegistry.registerModules([
  ClientSideRowModelModule,
  DateEditorModule,
  DateFilterModule,
  ColumnAutoSizeModule,
  RowSelectionModule
]);

const DATE_FILTER_PARAMS = [
  'active_on__gte',
  'active_on__lte',
  'active_on__isnull',
  'active_on__neq',
  'active_until__gte',
  'active_until__lte',
  'active_until__isnull',
  'active_until__neq'
];

// Maps AG Grid column field names to the backend ordering field names
const COLUMN_ORDERING_MAP: Record<string, string> = {
  program_name: 'program__name',
  jurisdiction_name: 'jurisdiction__name',
  insurance_provider_name: 'insurance_provider__external_name',
  payment_type: 'payment_type',
  insurance_type: 'insurance_type',
  prior_auth_required: 'prior_auth_required',
  active_on: 'active_on',
  active_until: 'active_until'
};

const PAYMENT_TYPE_OPTIONS: OptionType<string>[] = [
  { value: 'insurance', label: 'Insurance' },
  { value: 'self-pay', label: 'Self-Pay' }
];

export const listTreatabilityMatchesDefaults = {
  direction: DIRECTION.DESC,
  ordering: 'created_at',
  page: '1'
};

const EMPTY_BULK_EDIT: BulkEditData = {
  payment_type: null,
  active_on: '',
  active_until: '',
  prior_auth_required: null
};

const ListTreatabilityMatches = (): JSX.Element | null => {
  const { addMessage, resolvedTheme, darkStyle } = useContext(AppContext);

  const gridTheme = useMemo(
    () => resolveGridTheme(resolvedTheme, darkStyle),
    [resolvedTheme, darkStyle]
  );
  const repo = TreatabilityMatchRepository();
  const programRepo = ProgramRepository();
  const jurisdictionRepo = JurisdictionRepository();
  const insuranceRepo = InsuranceRepository();
  const [searchParams, setSearchParams] = useSearchParams();

  // ── Reference data ────────────────────────────────────────────────────────
  const [programs, setPrograms] = useState<Program[]>([]);
  const [jurisdictions, setJurisdictions] = useState<Jurisdiction[]>([]);
  const [providers, setProviders] = useState<InsuranceProvider[]>([]);
  const [insuranceTypes, setInsuranceTypes] = useState<InsurancePlanType[]>([]);
  const [refDataLoaded, setRefDataLoaded] = useState(false);

  useEffect(() => {
    Promise.all([
      programRepo.getAllPrograms(),
      jurisdictionRepo.getJurisdictions(),
      insuranceRepo.getInsuranceProviders(),
      insuranceRepo.getPlanTypes()
    ]).then(([programsResult, jurisdictionsResult, providersResult, insuranceTypesResult]) => {
      if (!programsResult.isError && programsResult.data)
        setPrograms(
          [...programsResult.data].sort((a, b) => {
            const nameCompare = a.name.localeCompare(b.name);
            if (nameCompare !== 0) return nameCompare;
            return (a.phase_type_name ?? '').localeCompare(b.phase_type_name ?? '');
          })
        );
      setJurisdictions(Array.isArray(jurisdictionsResult) ? jurisdictionsResult : []);
      if (!providersResult.isError && providersResult.data) setProviders(providersResult.data);
      if (!insuranceTypesResult.isError && insuranceTypesResult.data)
        setInsuranceTypes(insuranceTypesResult.data);
      setRefDataLoaded(true);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Filter state ───────────────────────────────────────────────────────────
  const [programFamilyFilter, setProgramFamilyFilter] = useState<OptionType<string>[]>([]);
  const [phaseTypeFilter, setPhaseTypeFilter] = useState<OptionType<string>[]>([]);
  const [jurisdictionFilter, setJurisdictionFilter] = useState<OptionType<string>[]>([]);
  const [providerFilter, setProviderFilter] = useState<OptionType<string>[]>([]);
  const [paymentTypeFilter, setPaymentTypeFilter] = useState<OptionType<string>[]>([]);
  const [insuranceTypeFilter, setInsuranceTypeFilter] = useState<OptionType<string>[]>([]);
  const [priorAuthFilter, setPriorAuthFilter] = useState<OptionType<string>[]>([]);

  // The plan-types endpoint includes a blank "No Insurance" option; drop empty
  // values and fall back to the value so tokens/search never render blank.
  const insuranceTypeOptions: OptionType<string>[] = useMemo(
    () =>
      insuranceTypes
        .filter((t) => t.value)
        .map((t) => ({ value: t.value, label: t.label || t.value })),
    [insuranceTypes]
  );

  // Initialise filter UI from URL once reference data is available
  const [filtersInitialized, setFiltersInitialized] = useState(false);
  useEffect(() => {
    if (!refDataLoaded || filtersInitialized) return;

    const familyNames = searchParams.get('program_family')?.split(',').filter(Boolean) ?? [];
    if (familyNames.length)
      setProgramFamilyFilter(familyNames.map((n) => ({ value: n, label: n })));

    const phaseTypeIds = searchParams.get('phase_type')?.split(',').filter(Boolean) ?? [];
    if (phaseTypeIds.length) {
      const seen = new Set<string>();
      const phaseOptions: OptionType<string>[] = [];
      programs.forEach((p) => {
        if (phaseTypeIds.includes(p.phase_type_id) && !seen.has(p.phase_type_id)) {
          seen.add(p.phase_type_id);
          phaseOptions.push({ value: p.phase_type_id, label: p.phase_type_name });
        }
      });
      setPhaseTypeFilter(phaseOptions);
    }

    const jurisdictionIds = searchParams.get('jurisdiction')?.split(',').filter(Boolean) ?? [];
    if (jurisdictionIds.length)
      setJurisdictionFilter(
        jurisdictions
          .filter((j) => jurisdictionIds.includes(String(j.id)))
          .map((j) => ({ value: String(j.id), label: j.name }))
      );

    const providerIds = searchParams.get('insurance_provider')?.split(',').filter(Boolean) ?? [];
    if (providerIds.length)
      setProviderFilter(
        providers
          .filter((p) => providerIds.includes(p.id))
          .map((p) => ({ value: p.id, label: p.external_name }))
      );

    const paymentTypes = searchParams.get('payment_type')?.split(',').filter(Boolean) ?? [];
    if (paymentTypes.length)
      setPaymentTypeFilter(PAYMENT_TYPE_OPTIONS.filter((o) => paymentTypes.includes(o.value)));

    const insuranceTypeValues =
      searchParams.get('insurance_type')?.split(',').filter(Boolean) ?? [];
    if (insuranceTypeValues.length)
      setInsuranceTypeFilter(
        insuranceTypeOptions.filter((o) => insuranceTypeValues.includes(o.value))
      );

    const priorAuthValues =
      searchParams.get('prior_auth_required')?.split(',').filter(Boolean) ?? [];
    if (priorAuthValues.length)
      setPriorAuthFilter(PRIOR_AUTH_OPTIONS_LIST.filter((o) => priorAuthValues.includes(o.value)));

    setFiltersInitialized(true);
  }, [
    refDataLoaded,
    filtersInitialized,
    programs,
    jurisdictions,
    providers,
    insuranceTypeOptions,
    searchParams
  ]);

  // ── Active query params ────────────────────────────────────────────────────
  const activeParams = {
    direction: searchParams.get('direction') ?? listTreatabilityMatchesDefaults.direction,
    ordering: searchParams.get('ordering') ?? listTreatabilityMatchesDefaults.ordering,
    page: searchParams.get('page') ?? listTreatabilityMatchesDefaults.page,
    program_family: searchParams.get('program_family') ?? '',
    phase_type: searchParams.get('phase_type') ?? '',
    jurisdiction: searchParams.get('jurisdiction') ?? '',
    insurance_provider: searchParams.get('insurance_provider') ?? '',
    payment_type: searchParams.get('payment_type') ?? '',
    insurance_type: searchParams.get('insurance_type') ?? '',
    prior_auth_required: searchParams.get('prior_auth_required') ?? '',
    active_on__gte: searchParams.get('active_on__gte') ?? '',
    active_on__lte: searchParams.get('active_on__lte') ?? '',
    active_on__isnull: searchParams.get('active_on__isnull') ?? '',
    active_on__neq: searchParams.get('active_on__neq') ?? '',
    active_until__gte: searchParams.get('active_until__gte') ?? '',
    active_until__lte: searchParams.get('active_until__lte') ?? '',
    active_until__isnull: searchParams.get('active_until__isnull') ?? '',
    active_until__neq: searchParams.get('active_until__neq') ?? ''
  };

  // ── Filter handlers ────────────────────────────────────────────────────────
  const applyMultiFilter = useCallback(
    (key: string, values: OptionType<string>[]) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        const csv = values.map((v) => v.value).join(',');
        if (csv) next.set(key, csv);
        else next.delete(key);
        next.set('page', '1');
        return next;
      });
    },
    [setSearchParams]
  );

  const handleProgramFamilyChange = (vals: OptionType<string>[]) => {
    setProgramFamilyFilter(vals);
    applyMultiFilter('program_family', vals);
  };
  const handlePhaseTypeChange = (vals: OptionType<string>[]) => {
    setPhaseTypeFilter(vals);
    applyMultiFilter('phase_type', vals);
  };
  const handleJurisdictionChange = (vals: OptionType<string>[]) => {
    setJurisdictionFilter(vals);
    applyMultiFilter('jurisdiction', vals);
  };
  const handleProviderChange = (vals: OptionType<string>[]) => {
    setProviderFilter(vals);
    applyMultiFilter('insurance_provider', vals);
  };
  const handlePaymentTypeChange = (vals: OptionType<string>[]) => {
    setPaymentTypeFilter(vals);
    applyMultiFilter('payment_type', vals);
  };
  const handleInsuranceTypeChange = (vals: OptionType<string>[]) => {
    setInsuranceTypeFilter(vals);
    applyMultiFilter('insurance_type', vals);
  };
  const handlePriorAuthChange = (vals: OptionType<string>[]) => {
    setPriorAuthFilter(vals);
    applyMultiFilter('prior_auth_required', vals);
  };

  const handleClearFilters = useCallback(() => {
    setProgramFamilyFilter([]);
    setPhaseTypeFilter([]);
    setJurisdictionFilter([]);
    setProviderFilter([]);
    setPaymentTypeFilter([]);
    setInsuranceTypeFilter([]);
    setPriorAuthFilter([]);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      [
        'program_family',
        'phase_type',
        'jurisdiction',
        'insurance_provider',
        'payment_type',
        'insurance_type',
        'prior_auth_required',
        ...DATE_FILTER_PARAMS
      ].forEach((k) => next.delete(k));
      next.set('page', '1');
      return next;
    });
    gridApiRef.current?.setFilterModel(null);
  }, [setSearchParams]);

  const hasActiveFilters =
    programFamilyFilter.length > 0 ||
    phaseTypeFilter.length > 0 ||
    jurisdictionFilter.length > 0 ||
    providerFilter.length > 0 ||
    paymentTypeFilter.length > 0 ||
    insuranceTypeFilter.length > 0 ||
    priorAuthFilter.length > 0 ||
    !!activeParams.active_on__gte ||
    !!activeParams.active_on__lte ||
    !!activeParams.active_until__gte ||
    !!activeParams.active_until__lte;

  // ── AG Grid ────────────────────────────────────────────────────────────────
  const gridApiRef = useRef<GridApi<TreatabilityMatch> | null>(null);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [bulkEditOpen, setBulkEditOpen] = useState(false);
  const [bulkAddOpen, setBulkAddOpen] = useState(false);
  const [bulkEditData, setBulkEditData] = useState<BulkEditData>(EMPTY_BULK_EDIT);

  const syncDateFiltersToSearchParams = useCallback(() => {
    const api = gridApiRef.current;
    if (!api) return;
    const model = api.getFilterModel() as Record<string, unknown>;

    // AG Grid sends dates as "YYYY-MM-DD HH:MM:SS"; strip the time so Django's
    // DateFilter receives a valid "YYYY-MM-DD" string.
    const toDateStr = (val: string | null | undefined) => val?.split(' ')[0] ?? '';

    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      DATE_FILTER_PARAMS.forEach((p) => next.delete(p));

      Object.entries(model).forEach(([field, value]) => {
        if (!value || typeof value !== 'object') return;
        const { type, dateFrom, dateTo } = value as {
          type?: string;
          dateFrom?: string;
          dateTo?: string;
        };
        if (field !== 'active_on' && field !== 'active_until') return;

        const from = toDateStr(dateFrom);
        const to = toDateStr(dateTo);

        if (type === 'inRange') {
          if (from) next.set(`${field}__gte`, from);
          if (to) next.set(`${field}__lte`, to);
        } else if (type === 'equals' && from) {
          next.set(`${field}__gte`, from);
          next.set(`${field}__lte`, from);
        } else if (type === 'greaterThan' && from) {
          next.set(`${field}__gte`, from);
        } else if (type === 'lessThan' && from) {
          next.set(`${field}__lte`, from);
        } else if (type === 'blank') {
          next.set(`${field}__isnull`, 'true');
        } else if (type === 'notBlank') {
          next.set(`${field}__isnull`, 'false');
        } else if (type === 'notEqual' && from) {
          next.set(`${field}__neq`, from);
        }
      });

      next.set('page', '1');
      return next;
    });
  }, [setSearchParams]);

  const handleSortChanged = useCallback(
    (event: SortChangedEvent<TreatabilityMatch>) => {
      const sortModel = event.api.getColumnState().find((col) => col.sort);
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        if (sortModel?.colId && COLUMN_ORDERING_MAP[sortModel.colId]) {
          next.set('ordering', COLUMN_ORDERING_MAP[sortModel.colId]);
          next.set('direction', sortModel.sort === 'asc' ? DIRECTION.ASC : DIRECTION.DESC);
        } else {
          next.set('ordering', listTreatabilityMatchesDefaults.ordering);
          next.set('direction', listTreatabilityMatchesDefaults.direction);
        }
        next.set('page', '1');
        return next;
      });
    },
    [setSearchParams]
  );

  const handleGridReady = useCallback((params: { api: GridApi<TreatabilityMatch> }) => {
    gridApiRef.current = params.api;
  }, []);

  const handleSelectionChanged = useCallback(() => {
    const api = gridApiRef.current;
    if (!api) return;
    setSelectedIds(api.getSelectedRows().map((row) => row.id));
  }, []);

  const columnDefs: ColDef<TreatabilityMatch>[] = [
    {
      field: 'program_name',
      headerName: 'Program',
      sortable: true,
      filter: false,
      flex: 2,
      valueGetter: (params) =>
        params.data ? `${params.data.program_name} - ${params.data.phase_display_name}` : ''
    },
    {
      field: 'phase_display_name',
      headerName: 'Phase',
      sortable: false,
      filter: false,
      flex: 1
    },
    {
      field: 'jurisdiction_name',
      headerName: 'Jurisdiction',
      sortable: true,
      filter: false,
      flex: 1
    },
    {
      field: 'insurance_provider_name',
      headerName: 'Insurance Provider',
      sortable: true,
      filter: false,
      flex: 2
    },
    { field: 'payment_type', headerName: 'Payment Type', sortable: true, filter: false, flex: 1 },
    {
      field: 'insurance_type',
      headerName: 'Insurance Type',
      sortable: true,
      filter: false,
      flex: 1
    },
    {
      field: 'prior_auth_required',
      headerName: 'Prior Auth',
      sortable: true,
      filter: false,
      flex: 1
    },
    {
      field: 'active_on',
      headerName: 'Active On',
      sortable: true,
      filter: 'agDateColumnFilter',
      flex: 1,
      editable: true,
      cellEditor: 'agDateStringCellEditor',
      valueFormatter: (params) => {
        if (!params.value) return '';
        const [y, m, d] = (params.value as string).split('-').map(Number);
        return new Date(y, m - 1, d).toLocaleDateString();
      }
    },
    {
      field: 'active_until',
      headerName: 'Active Until',
      sortable: true,
      filter: 'agDateColumnFilter',
      flex: 1,
      editable: true,
      cellEditor: 'agDateStringCellEditor',
      valueFormatter: (params) => {
        if (!params.value) return '';
        const [y, m, d] = (params.value as string).split('-').map(Number);
        return new Date(y, m - 1, d).toLocaleDateString();
      }
    },
    {
      field: 'id',
      headerName: '',
      sortable: false,
      filter: false,
      width: 70,
      resizable: false,
      cellRenderer: (params: ICellRendererParams<TreatabilityMatch>) => (
        <div className="flex items-center gap-2 h-full">
          <AuditLogLink entityType="treatabilitymatch" entityId={params.value} />
          <button
            className="text-red-600 hover:text-red-900 text-sm"
            onClick={() => handleDelete(params.value)}>
            <FontAwesomeIcon icon={faTrash} />
          </button>
        </div>
      )
    }
  ];

  const handleDelete = useCallback(
    async (id: string) => {
      const result = await repo.deleteTreatabilityMatch(id);
      if (result.isError) {
        addMessage({ level: 'error', text: 'Failed to delete record.' });
      } else {
        addMessage({ level: 'success', text: 'Deleted successfully.' });
        refetch();
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [repo, addMessage]
  );

  const handleCellValueChanged = useCallback(
    async (event: CellValueChangedEvent<TreatabilityMatch>) => {
      const { data, colDef, newValue } = event;
      if (!colDef.field || newValue === event.oldValue) return;
      try {
        await repo.updateTreatabilityMatch(data.id, { [colDef.field]: newValue || null });
        addMessage({ level: 'success', text: 'Updated successfully.' });
      } catch (e) {
        addMessage({ level: 'error', text: typeof e === 'string' ? e : 'Failed to update.' });
        refetch();
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [repo, addMessage]
  );

  // ── Data query ────────────────────────────────────────────────────────────
  const getMatchesQuery = useAPI({
    queryKey: ['getTreatabilityMatches', activeParams],
    queryFn: async () => {
      const {
        direction,
        ordering,
        page,
        program_family,
        phase_type,
        jurisdiction,
        insurance_provider,
        payment_type,
        insurance_type,
        prior_auth_required,
        active_on__gte,
        active_on__lte,
        active_on__isnull,
        active_on__neq,
        active_until__gte,
        active_until__lte,
        active_until__isnull,
        active_until__neq
      } = activeParams;
      return await repo.listTreatabilityMatches({
        ordering: `${direction === DIRECTION.ASC ? '' : '-'}${ordering}`,
        page,
        ...(program_family && { program_family }),
        ...(phase_type && { phase_type }),
        ...(jurisdiction && { jurisdiction }),
        ...(insurance_provider && { insurance_provider }),
        ...(payment_type && { payment_type }),
        ...(insurance_type && { insurance_type }),
        ...(prior_auth_required && { prior_auth_required }),
        ...(active_on__gte && { active_on__gte }),
        ...(active_on__lte && { active_on__lte }),
        ...(active_on__isnull && { active_on__isnull }),
        ...(active_on__neq && { active_on__neq }),
        ...(active_until__gte && { active_until__gte }),
        ...(active_until__lte && { active_until__lte }),
        ...(active_until__isnull && { active_until__isnull }),
        ...(active_until__neq && { active_until__neq })
      });
    }
  });

  const { data, isPending, isSuccess, isError, error, refetch } = getMatchesQuery;
  const matches = data?.data?.results || [];
  const totalCount = data?.data?.count ?? 0;

  const [totalPages, setTotalPages] = useState<number>(0);
  useEffect(() => {
    if (isSuccess) {
      setTotalPages(data.data?.total_pages ?? 0);
    } else if (isError) {
      addMessage(mapAxiosErrorMessages(error as AxiosError));
    }
  }, [addMessage, data?.data?.total_pages, error, isError, isSuccess]);

  const { currentPage, goToPage, nextPage, prevPage } = usePagination(
    +activeParams.page,
    totalPages
  );

  useEffect(() => {
    if (currentPage !== +listTreatabilityMatchesDefaults.page)
      setSearchParams((currentParams) => {
        currentParams.set('page', currentPage.toString());
        return currentParams;
      });
    else
      setSearchParams((currentParams) => {
        currentParams.delete('page');
        return currentParams;
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  // ── Bulk edit submit ───────────────────────────────────────────────────────
  const handleBulkEditSubmit = async () => {
    const payload: TreatabilityMatchBulkUpdatePayload = { ids: selectedIds, data: {} };
    if (bulkEditData.payment_type?.value)
      payload.data.payment_type = bulkEditData.payment_type.value;
    if (bulkEditData.active_on) payload.data.active_on = bulkEditData.active_on;
    if (bulkEditData.active_until) payload.data.active_until = bulkEditData.active_until;
    if (bulkEditData.prior_auth_required?.value)
      payload.data.prior_auth_required = bulkEditData.prior_auth_required.value;

    const result = await repo.bulkUpdateTreatabilityMatches(payload);
    if (result.isError) {
      addMessage({ level: 'error', text: 'Failed to bulk update.' });
    } else {
      addMessage({ level: 'success', text: `Updated ${result.data?.updated ?? 0} records.` });
      setBulkEditOpen(false);
      setBulkEditData(EMPTY_BULK_EDIT);
      refetch();
    }
  };

  // ── Option lists for filter bar ───────────────────────────────────────────
  const programFamilyOptions: OptionType<string>[] = Array.from(
    new Map(programs.map((p) => [p.name, p.name])).entries()
  ).map(([name]) => ({ value: name, label: name }));

  const phaseTypeOptions: OptionType<string>[] = (() => {
    const seen = new Set<string>();
    return programs
      .filter((p) => {
        if (seen.has(p.phase_type_id)) return false;
        seen.add(p.phase_type_id);
        return true;
      })
      .map((p) => ({ value: p.phase_type_id, label: p.phase_type_name }))
      .sort((a, b) => a.label.localeCompare(b.label));
  })();

  const jurisdictionOptions: OptionType<string>[] = jurisdictions.map((j) => ({
    value: String(j.id),
    label: j.name
  }));
  const providerOptions: OptionType<string>[] = providers.map((p) => ({
    value: p.id,
    label: p.external_name
  }));

  return (
    <Container type="full-width-page">
      {/* ── Page header ── */}
      <div className="flex items-center justify-between mb-3">
        <PageHeader headingText="Treatability Matches" />
        <div className="flex gap-2">
          {selectedIds.length > 0 && (
            <Button size="sm" onClick={() => setBulkEditOpen(true)}>
              Edit Selected ({selectedIds.length})
            </Button>
          )}
          <Button size="sm" onClick={() => setBulkAddOpen(true)}>
            Add Matches
          </Button>
        </div>
      </div>

      {/* ── Filter bar ── */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-3">
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4 xl:grid-cols-7">
          <div className="relative">
            <AutocompleteMultiSelect
              label="Program Family"
              options={programFamilyOptions}
              selectedOptions={programFamilyFilter}
              onChange={handleProgramFamilyChange}
              placeholder="All families"
              debounce={false}
            />
          </div>
          <div className="relative">
            <AutocompleteMultiSelect
              label="Phase"
              options={phaseTypeOptions}
              selectedOptions={phaseTypeFilter}
              onChange={handlePhaseTypeChange}
              placeholder="All phases"
              debounce={false}
            />
          </div>
          <div className="relative">
            <AutocompleteMultiSelect
              label="Jurisdiction"
              options={jurisdictionOptions}
              selectedOptions={jurisdictionFilter}
              onChange={handleJurisdictionChange}
              placeholder="All jurisdictions"
              debounce={false}
            />
          </div>
          <div className="relative">
            <AutocompleteMultiSelect
              label="Insurance Provider"
              options={providerOptions}
              selectedOptions={providerFilter}
              onChange={handleProviderChange}
              placeholder="All providers"
              debounce={false}
            />
          </div>
          <div className="relative">
            <AutocompleteMultiSelect
              label="Payment Type"
              options={PAYMENT_TYPE_OPTIONS}
              selectedOptions={paymentTypeFilter}
              onChange={handlePaymentTypeChange}
              placeholder="All types"
              debounce={false}
            />
          </div>
          <div className="relative">
            <AutocompleteMultiSelect
              label="Insurance Type"
              options={insuranceTypeOptions}
              selectedOptions={insuranceTypeFilter}
              onChange={handleInsuranceTypeChange}
              placeholder="All insurance types"
              debounce={false}
            />
          </div>
          <div className="relative">
            <AutocompleteMultiSelect
              label="Prior Auth"
              options={PRIOR_AUTH_OPTIONS_LIST}
              selectedOptions={priorAuthFilter}
              onChange={handlePriorAuthChange}
              placeholder="All prior auth"
              debounce={false}
            />
          </div>
        </div>

        <div className="flex items-center justify-between mt-3">
          <span className="text-sm text-gray-500">
            {isPending ? 'Loading…' : `${totalCount} result${totalCount !== 1 ? 's' : ''}`}
          </span>
          {hasActiveFilters && (
            <button
              className="text-sm text-indigo-600 hover:text-indigo-800 font-medium"
              onClick={handleClearFilters}>
              Clear filters
            </button>
          )}
        </div>
      </div>

      {/* ── Grid ── */}
      {isPending && <Spinner />}
      <Card className="overflow-hidden p-0" paddingClass="p-0">
        <div className="w-full">
          <AgGridReact<TreatabilityMatch>
            theme={gridTheme}
            rowData={matches}
            columnDefs={columnDefs}
            rowSelection={{
              mode: 'multiRow',
              checkboxes: true,
              headerCheckbox: true,
              enableClickSelection: true
            }}
            selectionColumnDef={{ width: 50, resizable: false }}
            pagination={false}
            domLayout="autoHeight"
            onFilterChanged={syncDateFiltersToSearchParams}
            onSortChanged={handleSortChanged}
            onGridReady={handleGridReady}
            onSelectionChanged={handleSelectionChanged}
            onCellValueChanged={handleCellValueChanged}
          />
        </div>
      </Card>

      <div className="mt-4">
        <Paginator
          currentPage={currentPage}
          goToPage={goToPage}
          nextPage={nextPage}
          prevPage={prevPage}
          totalPages={totalPages}
        />
      </div>

      <BulkEditModal
        open={bulkEditOpen}
        onClose={() => setBulkEditOpen(false)}
        data={bulkEditData}
        onChange={setBulkEditData}
        onSubmit={handleBulkEditSubmit}
        selectedCount={selectedIds.length}
      />

      <BulkAddModal
        open={bulkAddOpen}
        onClose={() => setBulkAddOpen(false)}
        repo={repo}
        addMessage={addMessage}
        onSuccess={refetch}
        programs={programs}
        jurisdictions={jurisdictions}
        providers={providers}
        insuranceTypes={insuranceTypes}
      />
    </Container>
  );
};

export default ListTreatabilityMatches;
